/**
 * My Property Requests Page (CREG-13)
 *
 * Allows individuals to track their property listing requests submitted
 * through Safaqat 24's FAL license program.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DomainIcon from '../../components/base/icons/domain/DomainIcon';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import {
  Loader2,
  Home,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileEdit,
  Building2,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import propertyRequestsService, {
  type PropertyRequest,
  type RequestStatus,
} from '../../services/propertyRequestsService';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { formatAmount } from '../../utils/numberFormat';

// Status configuration with colors and icons
const STATUS_CONFIG: Record<RequestStatus, {
  bgColor: string;
  textColor: string;
  borderColor: string;
  icon: typeof Clock;
  labelKey: string;
}> = {
  draft: {
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    textColor: 'text-gray-700 dark:text-gray-300',
    borderColor: 'border-gray-300 dark:border-gray-600',
    icon: FileEdit,
    labelKey: 'status.draft',
  },
  pending_review: {
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    textColor: 'text-amber-700 dark:text-amber-400',
    borderColor: 'border-amber-300 dark:border-amber-700',
    icon: Clock,
    labelKey: 'status.pending',
  },
  approved: {
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-400',
    borderColor: 'border-green-300 dark:border-green-700',
    icon: CheckCircle,
    labelKey: 'status.approved',
  },
  rejected: {
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-700 dark:text-red-400',
    borderColor: 'border-red-300 dark:border-red-700',
    icon: XCircle,
    labelKey: 'status.rejected',
  },
  listed: {
    bgColor: 'bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/30',
    textColor: 'text-[#003930] dark:text-[#1B7566]',
    borderColor: 'border-[var(--pv-color-accent-soft)] dark:border-[#004D40]',
    icon: ExternalLink,
    labelKey: 'status.listed',
  },
  completed: {
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-400',
    borderColor: 'border-blue-300 dark:border-blue-700',
    icon: CheckCircle,
    labelKey: 'status.completed',
  },
};

// Property type labels with English and Arabic
const PROPERTY_TYPES: Record<string, { en: string; ar: string }> = {
  VILLA: { en: 'Villa', ar: 'فيلا' },
  APARTMENT: { en: 'Apartment', ar: 'شقة' },
  FLOOR: { en: 'Floor', ar: 'دور' },
  RESIDENTIAL_LAND: { en: 'Residential Land', ar: 'أرض سكنية' },
  COMMERCIAL_LAND: { en: 'Commercial Land', ar: 'أرض تجارية' },
  COMMERCIAL_BUILDING: { en: 'Commercial Building', ar: 'عمارة تجارية' },
  OFFICE: { en: 'Office', ar: 'مكتب' },
  WAREHOUSE: { en: 'Warehouse', ar: 'مستودع' },
  SHOP: { en: 'Shop', ar: 'محل' },
  LAND: { en: 'Land', ar: 'أرض' },
};

/**
 * Request Card Component - Expandable card showing request details
 */
function RequestCard({
  request,
  isExpanded,
  onToggle,
  isRTL,
}: {
  request: PropertyRequest;
  isExpanded: boolean;
  onToggle: () => void;
  isRTL: boolean;
}) {
  const { t } = useTranslation('propertyRequests');
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending_review;
  const StatusIcon = statusConfig.icon;
  const propertyType = PROPERTY_TYPES[request.propertyType] || {
    en: request.propertyType,
    ar: request.propertyType,
  };

  const formatPrice = (price: number) => {
    return formatAmount(price);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-[var(--pv-border-soft)] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          {/* Property Icon */}
          <div className="w-12 h-12 bg-[var(--pv-color-primary)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-[var(--pv-color-primary)]" />
          </div>

          {/* Property Info */}
          <div className={`text-${isRTL ? 'right' : 'left'}`}>
            <p className="font-semibold text-[var(--pv-color-text)]">
              {isRTL ? (request.titleAr || request.title) : request.title}
            </p>
            <p className="text-sm text-[var(--pv-color-text-muted)]">
              {isRTL ? propertyType.ar : propertyType.en}
              <span className="mx-2">-</span>
              {request.listingType === 'SALE'
                ? t('listingType.sale')
                : t('listingType.rent')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Status Badge */}
          <div
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${statusConfig.bgColor} ${statusConfig.textColor}`}
          >
            <StatusIcon className="w-4 h-4" />
            <span>{t(statusConfig.labelKey)}</span>
          </div>

          {/* Expand/Collapse */}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[var(--pv-color-text-muted)]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[var(--pv-color-text-muted)]" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 border-t border-[var(--pv-border-soft)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Request Details */}
            <div className="space-y-4">
              {request.description && (
                <div>
                  <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                    {t('description')}
                  </p>
                  <p className="text-sm text-[var(--pv-color-text)]">
                    {isRTL ? (request.descriptionAr || request.description) : request.description}
                  </p>
                </div>
              )}

              {request.price && (
                <div>
                  <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                    {t('expectedPrice')}
                  </p>
                  <p className="text-lg font-bold text-[var(--pv-color-primary)]">
                    {formatPrice(request.price)} {t('sar')}
                    {request.priceNegotiable && (
                      <span className="text-sm font-normal text-[var(--pv-color-text-muted)] ml-2">
                        ({t('negotiable')})
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                  {t('contact')}
                </p>
                <p className="text-sm text-[var(--pv-color-text)]">
                  {isRTL ? (request.ownerNameAr || request.ownerName) : request.ownerName}
                </p>
                <p className="text-sm text-[var(--pv-color-text-muted)]">
                  {request.ownerPhone}
                  {request.ownerEmail && ` - ${request.ownerEmail}`}
                </p>
              </div>

              {/* Specifications */}
              {(request.bedrooms || request.bathrooms || request.sqft) && (
                <div>
                  <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                    {t('specifications')}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-[var(--pv-color-text)]">
                    {request.bedrooms && (
                      <span className="inline-flex items-center gap-1">
                        <DomainIcon id="bedroom" size={15} className="shrink-0" />
                        {request.bedrooms} {t('bedrooms')}
                      </span>
                    )}
                    {request.bathrooms && (
                      <span className="inline-flex items-center gap-1">
                        <DomainIcon id="bathroom" size={15} className="shrink-0" />
                        {request.bathrooms} {t('bathrooms')}
                      </span>
                    )}
                    {request.sqft && (
                      <span>
                        <i className="ri-ruler-line mr-1"></i>
                        {formatPrice(request.sqft)} {t('sqm')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Timeline */}
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                  {t('submitted')}
                </p>
                <p className="text-sm text-[var(--pv-color-text)]">
                  {format(new Date(request.createdAt), 'PPp', {
                    locale: isRTL ? ar : undefined,
                  })}
                </p>
              </div>

              {request.reviewedAt && (
                <div>
                  <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                    {t('reviewed')}
                  </p>
                  <p className="text-sm text-[var(--pv-color-text)]">
                    {format(new Date(request.reviewedAt), 'PPp', {
                      locale: isRTL ? ar : undefined,
                    })}
                  </p>
                </div>
              )}

              {request.rejectionReason && (
                <div>
                  <p className="text-xs font-medium text-[var(--pv-color-text-muted)] mb-1 uppercase tracking-wide">
                    {t('rejectionReason')}
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    {request.rejectionReason}
                  </p>
                </div>
              )}

              {request.status === 'listed' && request.propertyId && (
                <div className="flex flex-wrap gap-3">
                  <a
                    href={`/property/${request.propertyId}`}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--pv-color-primary)] rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('viewListing')}
                  </a>
                  <a
                    href={`/complaints?propertyId=${request.propertyId}`}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--pv-color-primary)] border border-[var(--pv-color-primary)] rounded-lg hover:bg-[var(--pv-color-primary)]/10 transition-colors"
                  >
                    {t('contactToEdit')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Main Page Component
 */
export default function MyPropertyRequestsPage() {
  const { t, i18n } = useTranslation('propertyRequests');
  const { session } = useAuth();
  const token = session?.token;
  const isRTL = i18n.language === 'ar';

  const [requests, setRequests] = useState<PropertyRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!token) {
      setError(t('error.notLoggedIn'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await propertyRequestsService.getMyRequests({}, token);
      setRequests(response.data);
    } catch (err) {
      console.error('Failed to fetch property requests:', err);
      setError(t('error.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return (
    <div
      className="min-h-screen bg-[var(--pv-color-surface)]"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <Navigation />

      {/* Page Header */}
      <header className="relative pt-8 pb-6">
        <div className="absolute inset-x-0 top-0 -z-10 h-[200px] bg-[var(--pv-color-surface-soft)]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-text)]">
                {t('title')}
              </h1>
              <p className="text-[var(--pv-color-text-muted)] mt-1">
                {t('subtitle')}
              </p>
            </div>
            <a
              href={token ? '/client-requests/new' : '/register?type=individual'}
              className="px-4 py-2 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all"
            >
              <i className="ri-add-line mr-2"></i>
              {t('newRequest')}
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--pv-color-primary)]" />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button
              onClick={fetchRequests}
              className="ml-auto text-sm font-medium underline hover:no-underline"
            >
              {t('error.retry')}
            </button>
          </div>
        )}

        {/* Requests List */}
        {!isLoading && !error && requests.length > 0 && (
          <div className="space-y-4">
            {requests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                isExpanded={expandedId === request.id}
                onToggle={() =>
                  setExpandedId(expandedId === request.id ? null : request.id)
                }
                isRTL={isRTL}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && requests.length === 0 && (
          <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-[var(--pv-border-soft)]">
            <div className="w-20 h-20 bg-[var(--pv-color-primary)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Home className="w-10 h-10 text-[var(--pv-color-primary)]" />
            </div>
            <h3 className="text-xl font-semibold text-[var(--pv-color-text)] mb-2">
              {t('empty.title')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)] mb-8 max-w-md mx-auto">
              {t('empty.description')}
            </p>
            <a
              href={token ? '/client-requests/new' : '/register?type=individual'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--pv-color-primary)] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
            >
              <i className="ri-add-line"></i>
              {t('empty.cta')}
            </a>
          </div>
        )}

        {/* Info Box */}
        {!isLoading && requests.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <i className="ri-information-line mr-2"></i>
              {t('info')}
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
